/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {
  useContext, useCallback,
} from 'react';
import {
  useMediaQuery,
  useTheme,
  Dialog,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Hidden,
  DialogContent,
} from '@material-ui/core';
import ProfileAvatarSection from 'components/ProfileAvatarSection';
import { Close as CloseIcon } from '@material-ui/icons';
import styled from 'styled-components';
import { selectLocale } from 'models/localization';
import toBase64 from 'models/helpers/toBase64';
import { useTranslation } from 'react-i18next';
import { UserSessionContext } from 'components/UserSession';
import { setOpen, setAvatarImage } from 'models/profileModal';
import { useDispatch, useSelector } from 'react-redux';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { DEFAULT_THEME_COLOR } from 'appenv';
import TextInput from './input/TextInput';
import ModalButton from './ModalButton';

const StyledAppBar = styled(AppBar)`
  position: relative;
`;

const StyledToolbar = styled(Toolbar)`
  @media (min-width: 960px) {
    flex-direction: row-reverse;
  }
`;

const AppBarTitle = styled(Typography)`
  margin-left: 16px;
  flex: 1;
`;

const ProfileTextField = styled(TextInput)`
  margin-bottom: 8px;

  label + & {
    margin-top: 24px;
  }
`;

const DesktopSaveButton = styled(ModalButton)`
  display: none;
  margin-top: 12px;
  margin-bottom: 16px;
  margin-left: 4px;
  width: calc(100% - 8px);

  @media (min-width: 960px) {
    display: block;
  }
`;

const MobileProfileAppBarButton = styled(Button)<{ themeColor: string }>`
  text-transform: initial;
  font-weight: bold;
  color: ${({ themeColor }) => (themeColor || DEFAULT_THEME_COLOR)}
`;

interface MobileProfileAppBarProps {
  onSave: () => void;
  onClose: () => void;
}

const MobileProfileAppBar = ({
  onSave,
  onClose,
}: MobileProfileAppBarProps) => {
  const { t } = useTranslation();
  const { pageNames: { profile }, themeColor } = useSelector((state) => state.eventConfigs);
  const locale = useSelector(selectLocale);
  const profilePageTitle = findAvailableLocale(profile || {}, locale) || t('pages.profile', 'Profile');

  return (
    <StyledAppBar color="transparent" elevation={0}>
      <StyledToolbar>
        <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
        <AppBarTitle variant="h6" align="center">
          {profilePageTitle}
        </AppBarTitle>
        <Hidden mdUp>
          <MobileProfileAppBarButton themeColor={themeColor} autoFocus onClick={onSave}>
            {t('save')}
          </MobileProfileAppBarButton>
        </Hidden>
      </StyledToolbar>
    </StyledAppBar>
  );
};

const ProfileModal = () => {
  const dispatch = useDispatch();
  const { userSession } = useContext(UserSessionContext);
  const theme = useTheme();
  const { t } = useTranslation();
  const open = useSelector((s) => (s as any).profileModal.open);
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    firstName,
    lastName,
    email,
    company,
    title,
  } = userSession || {};
  const onClose = useCallback(() => {
    dispatch(setOpen(false));
  }, [dispatch]);
  const onSave = useCallback(() => {
    dispatch(setOpen(false));
  }, [dispatch]);
  const avatar = useSelector((s) => (s as any).profileModal.avatarImage);

  const onAvatarChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const imageFile = event.target.files[0];
    const encodedImage = await toBase64(imageFile);
    dispatch(setAvatarImage(encodedImage.toString()));
  };

  return (
    <Dialog
      fullScreen={fullScreen}
      open={open}
      fullWidth
      onClose={onClose}
    >
      <MobileProfileAppBar
        onClose={onClose}
        onSave={onSave}
      />
      <DialogContent>
        <ProfileAvatarSection
          showEditButton
          avatar={avatar}
          onAvatarChange={onAvatarChange}
        />
        <ProfileTextField
          required
          label="First Name"
          defaultValue={firstName}
        />
        <ProfileTextField
          label="Last Name"
          defaultValue={lastName}
        />
        <ProfileTextField
          label="Title"
          defaultValue={title}
        />
        <ProfileTextField
          label="Company"
          defaultValue={company}
        />
        <ProfileTextField
          required
          type="email"
          label="Email"
          defaultValue={email}
        />
        <DesktopSaveButton
          type="button"
          onClick={onSave}
        >
          {t('save')}
        </DesktopSaveButton>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;
